import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import Button from "../Button/Button";
import Input from "../Input/Input";
import Spinner from "../Spinner/Spinner";
import Divider from "../Divider/Divider";
import Checkbox from "../Checkbox/Checkbox";

export default function AddItemForm({ addItem, editItem, list, onClose }) {
    const navigate = useNavigate();
    const { user } = useAuth();

    const [formData, setFormData] = useState({
        name: "",
        url: "",
        size: "",
        color: "",
        notes: "",
        imageUrl: "",
        price: "",
        isGiftCard: false,
        listId: list?.id,
    });

    const [errors, setErrors] = useState({});
    const [imageError, setImageError] = useState(false);
    const [loading, setLoading] = useState(false);

    const isUnchanged = useMemo(() => {
        if (!editItem) return false;
        return (
            formData.name === (editItem.name || "") &&
            formData.url === (editItem.url || "") &&
            formData.size === (editItem.size || "") &&
            formData.color === (editItem.color || "") &&
            formData.notes === (editItem.notes || "") &&
            formData.imageUrl === (editItem.imageUrl || "") &&
            formData.isGiftCard === (editItem.isGiftCard || false) &&
            formData.price === (editItem.price || "")
        );
    }, [formData, editItem]);

    // Populate existing item data when editing
    useEffect(() => {
        if (editItem) {
            setFormData({
                name: editItem.name || "",
                url: editItem.url || "",
                size: editItem.size || "",
                color: editItem.color || "",
                notes: editItem.notes || "",
                imageUrl: editItem.imageUrl || "",
                price: editItem.price || "",
                isGiftCard: editItem.isGiftCard || false,
                listId: editItem.listId || list?.id,
            });
        }
    }, [editItem, list]);

    // URL Validation
    const validateURL = (url) => {
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    };

    // Fetch product metadata from backend
    const fetchProductFromUrl = async (url) => {
        if (!url) return;
        setLoading(true);
        setImageError(false);
        try {
            const res = await fetch(
                "https://us-central1-giftloop-468a3.cloudfunctions.net/api/fetch-product",
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ url }),
                }
            );

            if (!res.ok) throw new Error("Failed to fetch product data");
            const data = await res.json();

            setFormData((prev) => ({
                ...prev,
                name: data.name || prev.name,
                price: data.price || prev.price,
                imageUrl: data.imageUrl || prev.imageUrl,
                color: data.color || prev.color,
                size: data.size || prev.size,
            }));
        } catch (err) {
            console.error("Failed to fetch product data:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!validateURL(formData.url)) return;
        const timeout = setTimeout(() => fetchProductFromUrl(formData.url), 600);
        return () => clearTimeout(timeout);
    }, [formData.url]);

    // Handle input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (name === "imageUrl") setImageError(false);
    };

    // Submit form
    const handleSubmit = (e) => {
        e.preventDefault();

        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = "Item Name is required";
        if (!formData.url.trim()) newErrors.url = "URL is required";
        else if (!validateURL(formData.url)) newErrors.url = "Enter a valid URL";

        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;

        // Determine member’s display name for addedBy
        const memberInfo = list.memberInfo?.find((m) => m.uid === user.uid);
        const memberName = memberInfo
            ? memberInfo.displayName
            : user?.firstName && user?.lastName
                ? `${user.firstName} ${user.lastName}`
                : user?.displayName || user?.email?.split("@")[0] || "Member";

        const itemToSubmit = {
            ...formData,
            id: editItem?.id,
            addedBy: {
                uid: user?.uid,
                name: memberName,
            },
            purchasedBy: editItem?.purchasedBy || [],
            listId: list?.id,
        };

        console.log("Submitting item:", itemToSubmit);

        addItem(itemToSubmit);
        if (onClose) onClose();
        else navigate(`/lists/${formData.listId}`);
    };

    const handleCancel = () => {
        if (onClose) onClose();
        else navigate(`/lists/${formData.listId}`);
    };

    return (
        <div>
            <h1 className="text-xl font-bold mb-2">
                {editItem ? "Edit Item" : "Add Item"}
            </h1>

            <p className="text-sm mb-2">
                Start by adding the product's URL and fill in any extra information
            </p>

            <Divider />

            <div className="flex flex-col md:flex-row gap-4">
                {/* Image Preview */}
                <div className="w-full md:w-1/2 aspect-[4/3] bg-surface rounded flex items-center justify-center overflow-hidden relative">
                    {loading ? (
                        <Spinner />
                    ) : formData.imageUrl && !imageError ? (
                        <a
                            href={formData.url || "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full h-full block"
                        >
                            <div
                                className="w-full h-full bg-center bg-cover transition-opacity duration-500"
                                style={{ backgroundImage: `url(${formData.imageUrl})` }}
                            />
                        </a>
                    ) : (
                        <span className="absolute">Image preview</span>
                    )}
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="w-full md:w-1/2 space-y-2">
                    <Input
                        label="URL"
                        name="url"
                        placeholder="https://www.amazon.com/dp/ASIN"
                        value={formData.url}
                        onChange={handleChange}
                        error={errors.url}
                        required
                    />

                    <Input
                        label="Product Name"
                        name="name"
                        placeholder="T-Shirt"
                        value={formData.name}
                        onChange={handleChange}
                        error={errors.name}
                        required
                    />

                    <Checkbox
                        label="This item is a gift card"
                        name="giftCardCheckbox"
                        checked={formData.isGiftCard}
                        onChange={(checked) =>
                            setFormData((prev) => ({ ...prev, isGiftCard: checked }))
                        }
                    />

                    {!formData.isGiftCard && (
                        <div className="flex flex-col sm:flex-row gap-4">

                            <Input
                                label="Price"
                                name="price"
                                placeholder="$0.00"
                                value={formData.price}
                                onChange={handleChange}
                                error={errors.price}
                                required
                            />
                            <Input
                                label="Size"
                                name="size"
                                placeholder="M"
                                value={formData.size}
                                onChange={handleChange}
                                className="flex-1"
                            />
                            <Input
                                label="Color"
                                name="color"
                                placeholder="Blue"
                                value={formData.color}
                                onChange={handleChange}
                                className="flex-1"
                            />
                        </div>
                    )}

                    <Input
                        label="Notes"
                        name="notes"
                        placeholder="Ex. If blue is sold out, the navy is great too!"
                        value={formData.notes}
                        onChange={handleChange}
                        error={errors.notes}
                        multiline
                    />

                    <div className="flex flex-row justify-end gap-4">
                        <Button type="button" onClick={handleCancel} variant="ghost">
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={editItem && isUnchanged}
                        >
                            {editItem ? "Update Item" : "Add Item"}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
